import {
  CoverageBoolean,
  CoverageDebrisRemovalValue,
  CoverageEarthquakeValue,
  CoverageEquipmentBreakdownValue,
  CoverageGolfCartValue,
  CoverageLossAssessmentValue,
  CoverageScheduledPersonalPropertyItem,
  CoverageSecondaryResidenceLiabilityAddress,
  CoverageWaterBackupAndSumpOverflowValue,
  debrisRemovalGraphQLToViewMap,
  earthquakeGraphQLToViewMap,
  equipmentBreakdownGraphQLToViewMap,
  identityFraudExpenseGraphQLToViewMap,
  golfCartGraphQLToViewMap,
  lossAssessmentGraphQLToViewMap,
  residenceBurglaryLimitGraphQLToViewMap,
  ResidenceBurglaryValue,
  waterBackupAndSumpOverflowGraphQLToViewMap,
  CoverageIdentityFraudExpenseValue,
} from "@covertree/new-business-graphql";
import { formatCurrency } from "@covertree/utils";
import { GetValue } from "../../types";
import { isEnumValue } from "../../utils";

const getGolfCartDamageAndLiabilityCoverageValueFormats: GetValue<
  CoverageGolfCartValue
> = (value) => {
  if (!value) return undefined;

  let preparedValue = value;

  if (isEnumValue(value, CoverageGolfCartValue)) {
    preparedValue = golfCartGraphQLToViewMap[value];
  }

  return {
    prefix: "Number of golf carts covered:",
    value: `${preparedValue}`,
    message: `${preparedValue} golf cart(s) covered`,
  };
};

const getEarthquakeCoverageValueFormats: GetValue<CoverageEarthquakeValue> = (
  value
) => {
  if (!value) return undefined;

  let preparedValue = value;

  if (isEnumValue(value, CoverageEarthquakeValue)) {
    preparedValue = earthquakeGraphQLToViewMap[value];
  }

  if (typeof value === "number") {
    preparedValue = `${value}%`;
  }

  return {
    prefix: "Deductible:",
    value: `${preparedValue}`,
    message: `Covered (Deductible: ${preparedValue})`,
  };
};

const getWaterBackupCoverageValueFormats: GetValue<
  CoverageWaterBackupAndSumpOverflowValue
> = (value) => {
  if (!value) return undefined;

  let preparedValue = value;

  if (isEnumValue(value, CoverageWaterBackupAndSumpOverflowValue)) {
    preparedValue = waterBackupAndSumpOverflowGraphQLToViewMap[value];
  } else {
    preparedValue = formatCurrency(value);
  }

  return {
    prefix: "Coverage Amount:",
    value: preparedValue,
    message: preparedValue,
  };
};

const getEquipmentBreakdownValueFormats: GetValue<
  CoverageEquipmentBreakdownValue
> = (value) => {
  if (!value) return undefined;

  let preparedValue = value;

  if (isEnumValue(value, CoverageEquipmentBreakdownValue)) {
    preparedValue = equipmentBreakdownGraphQLToViewMap[value];
  } else {
    preparedValue = formatCurrency(value);
  }

  return {
    prefix: "Coverage Amount:",
    value: preparedValue,
    message: `${preparedValue} covered`,
  };
};

const getIdentityFraudExpenseValueFormats: GetValue<
  CoverageIdentityFraudExpenseValue
> = (value) => {
  if (!value) return undefined;

  let preparedValue = value;

  if (isEnumValue(value, CoverageIdentityFraudExpenseValue)) {
    preparedValue = identityFraudExpenseGraphQLToViewMap[value];
  } else {
    preparedValue = formatCurrency(value);
  }

  return {
    prefix: "Coverage Amount:",
    value: preparedValue,
    message: `${preparedValue} covered`,
  };
};

const getResidenceBurglaryValueFormats: GetValue<ResidenceBurglaryValue> = (
  value
) => {
  if (!value) return undefined;

  let preparedValue = value;

  if (isEnumValue(value, ResidenceBurglaryValue)) {
    preparedValue = residenceBurglaryLimitGraphQLToViewMap[value];
  } else {
    preparedValue = formatCurrency(value);
  }

  return {
    prefix: "Coverage Amount:",
    value: preparedValue,
    message: `${preparedValue} covered`,
  };
};

const getLossAssessmentValueFormats: GetValue<CoverageLossAssessmentValue> = (
  value
) => {
  if (!value) return undefined;

  let preparedValue = value;

  if (isEnumValue(value, CoverageLossAssessmentValue)) {
    preparedValue = lossAssessmentGraphQLToViewMap[value];
  } else {
    preparedValue = formatCurrency(value);
  }

  return {
    value: preparedValue,
  };
};

const getDebrisRemovalValueFormats: GetValue<CoverageDebrisRemovalValue> = (
  value
) => {
  if (!value) return undefined;

  let preparedValue = value;

  if (isEnumValue(value, CoverageDebrisRemovalValue)) {
    preparedValue = debrisRemovalGraphQLToViewMap[value];
  } else {
    preparedValue = formatCurrency(value);
  }

  return {
    value: preparedValue,
  };
};

const getValuableItemProtectionValueFormats: GetValue<
  CoverageScheduledPersonalPropertyItem[]
> = (value) => (value ? { message: "Included" } : undefined);

const getSecondaryResidenceLiabilityValueFormats: GetValue<
  CoverageSecondaryResidenceLiabilityAddress[]
> = (value) => (value ? { message: "Included" } : undefined);

const getVacationRentalCoverageValueFormats: GetValue<CoverageBoolean> = (
  value
) => (value ? { message: "Covered" } : undefined);

const getIncidentalFarmingValueFormats: GetValue<CoverageBoolean> = (value) =>
  value ? { message: "Covered" } : undefined;

const getEnhancedCoverageValueFormats: GetValue<CoverageBoolean> = (value) =>
  value ? { message: "Covered" } : undefined;

const getTripCollisionCoverageValueFormats: GetValue<CoverageBoolean> = (
  value
) => (value ? { message: "Covered for 30 days" } : undefined);

const getMineSubsidenceValueFormats: GetValue<CoverageBoolean> = (value) =>
  value ? { message: "Covered" } : undefined;

const getMineSubsidenceScheduledValueFormats: GetValue<CoverageBoolean> = (
  value
) => (value ? { message: "Included" } : undefined);

const getBuildersRiskValueFormats: GetValue<CoverageBoolean> = (value) =>
  value ? { message: "Covered" } : undefined;

const getSinkholeCoverageValueFormats: GetValue<CoverageBoolean> = (value) =>
  value ? { message: "Covered" } : undefined;

const getVandalismOrMaliciousMischiefValueFormats: GetValue<CoverageBoolean> = (
  value
) => (value ? { message: "Covered" } : undefined);

const getWindstormOrHailExclusionValueFormats: GetValue<CoverageBoolean> = (
  value
) => (value ? { message: "Excluded" } : undefined);

const getFortifiedRoofUpgradeValueFormats: GetValue<CoverageBoolean> = (
  value
) => (value ? { message: "Upgraded" } : undefined);

export {
  getGolfCartDamageAndLiabilityCoverageValueFormats,
  getEarthquakeCoverageValueFormats,
  getVacationRentalCoverageValueFormats,
  getWaterBackupCoverageValueFormats,
  getLossAssessmentValueFormats,
  getDebrisRemovalValueFormats,
  getIncidentalFarmingValueFormats,
  getEnhancedCoverageValueFormats,
  getTripCollisionCoverageValueFormats,
  getMineSubsidenceValueFormats,
  getMineSubsidenceScheduledValueFormats,
  getEquipmentBreakdownValueFormats,
  getIdentityFraudExpenseValueFormats,
  getValuableItemProtectionValueFormats,
  getSinkholeCoverageValueFormats,
  getResidenceBurglaryValueFormats,
  getBuildersRiskValueFormats,
  getSecondaryResidenceLiabilityValueFormats,
  getVandalismOrMaliciousMischiefValueFormats,
  getWindstormOrHailExclusionValueFormats,
  getFortifiedRoofUpgradeValueFormats,
};
